import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Minus, Plus } from "lucide-react";
import type { BookingSheetFormProps } from "./types";

function Stepper({ value, onChange, min = 1, max = 99, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-muted-foreground mr-1">{label}</span>}
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-8 text-center text-sm font-medium">{value}</span>
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

interface BookingSheetTrainingFormProps extends BookingSheetFormProps {
  coaches: any[];
  paymentMethods: { value: string; label: string }[];
}

export default function BookingSheetTrainingForm({
  formState,
  onFieldChange,
  coaches,
  paymentMethods,
}: BookingSheetTrainingFormProps) {
  const { t } = useTranslation("admin");
  const {
    trainingType,
    coachId,
    trainingPlayers,
    trainingMinLevel,
    trainingMaxLevel,
    trainingRequireLevel,
    giveFeedback,
    paymentMethod,
    hideTraining,
    trainingTitle,
  } = formState;

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div>
        <Label className="text-xs">{t("bookingSheet.trainingType")}</Label>
        <Select value={trainingType} onValueChange={(v) => onFieldChange("trainingType", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="group">{t("bookingSheet.group")}</SelectItem>
            <SelectItem value="private">{t("bookingSheet.private")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.coach")}</Label>
        <Select value={coachId} onValueChange={(v) => onFieldChange("coachId", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue placeholder={t("bookingSheet.selectCoach")} /></SelectTrigger>
          <SelectContent>
            {coaches.map((c: any) => (
              <SelectItem key={c.user_id} value={c.user_id}>
                {(c as any).profiles?.full_name || t("bookingSheet.coach")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.playerCount")}</Label>
        <Stepper value={trainingPlayers} onChange={(v) => onFieldChange("trainingPlayers", v)} min={1} max={20} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("bookingSheet.levelFrom")}</Label>
          <Stepper value={trainingMinLevel} onChange={(v) => onFieldChange("trainingMinLevel", v)} min={1} max={10} />
        </div>
        <div>
          <Label className="text-xs">{t("bookingSheet.levelTo")}</Label>
          <Stepper value={trainingMaxLevel} onChange={(v) => onFieldChange("trainingMaxLevel", v)} min={1} max={10} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.requireLevel")}</Label>
        <Switch checked={trainingRequireLevel} onCheckedChange={(v) => onFieldChange("trainingRequireLevel", v)} />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.giveFeedback")}</Label>
        <Switch checked={giveFeedback} onCheckedChange={(v) => onFieldChange("giveFeedback", v)} />
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.payment")}</Label>
        <Select value={paymentMethod} onValueChange={(v) => onFieldChange("paymentMethod", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {paymentMethods.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.hideTraining")}</Label>
        <Switch checked={hideTraining} onCheckedChange={(v) => onFieldChange("hideTraining", v)} />
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.title")}</Label>
        <Input value={trainingTitle} onChange={(e) => onFieldChange("trainingTitle", e.target.value)} className="h-9 text-sm" placeholder={t("bookingSheet.titlePlaceholder")} />
      </div>
    </div>
  );
}
